import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { FaFileInvoiceDollar, FaInbox, FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { ManagementPurchaseOrderCard } from "../../../models/Management";
import { crm, Pill } from "../../../theme";
import { canManagePurchaseOrders } from "../../purchase-order/permissions";

const usdFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString("pt-BR") : undefined);

// Coluna "Pedidos": POs ainda sem processo. Não recebe drop — vira processo pelo botão "Criar processo".
const PurchaseOrderColumn = ({ purchaseOrders }: { purchaseOrders: ManagementPurchaseOrderCard[] }) => {
  const navigate = useNavigate();
  const auth = useAuthUser<any>();
  const canManage = canManagePurchaseOrders(auth);

  return (
    <Flex
      flexDir="column"
      minW="300px"
      maxW="300px"
      h="100%"
      bg={crm.columnBg}
      borderRadius="14px"
      border="2px solid transparent"
      overflow="hidden"
    >
      <Box h="3px" bg={crm.textMuted} borderRadius="14px 14px 0 0" />

      <Flex align="center" justify="space-between" px="14px" py="10px">
        <Flex align="center" gap="8px">
          <FaFileInvoiceDollar color={crm.textMuted} size={12} />
          <Text fontWeight="700" fontSize="sm" color={crm.text}>
            Pedidos (PO)
          </Text>
        </Flex>
        <Pill bg={crm.surface} color={crm.textMuted}>
          {purchaseOrders.length}
        </Pill>
      </Flex>

      <Flex flexDir="column" gap="12px" p="12px" overflowY="auto" flex="1" minH="120px">
        {purchaseOrders.map((po) => {
          const shipment = fmtDate(po.shipmentDate);
          return (
            <Box
              key={po.id}
              onClick={() => navigate(`/savePurchaseOrders?id=${po.id}`)}
              bg={crm.surface}
              borderRadius="10px"
              border={`1px dashed ${crm.border}`}
              boxShadow={crm.shadowSm}
              p="12px"
              cursor="pointer"
              _hover={{ boxShadow: crm.shadowMd, borderColor: crm.brand }}
              transition="all .12s ease"
            >
              <Flex justify="space-between" align="flex-start" gap="8px">
                <Text fontWeight="600" color={crm.text} fontSize="sm">
                  PO {po.poNumber}
                </Text>
                {po.totalFobValue > 0 && (
                  <Pill bg={crm.brandSoft} color={crm.brand}>
                    {usdFmt.format(po.totalFobValue)}
                  </Pill>
                )}
              </Flex>
              {po.client && (
                <Text fontSize="sm" color={crm.text} mt="6px" noOfLines={1}>
                  {po.client}
                </Text>
              )}
              {po.supplier && (
                <Text fontSize="xs" color={crm.textMuted} mt="2px" noOfLines={1}>
                  {po.supplier}
                </Text>
              )}
              <Flex mt="10px" justify="space-between" align="center" gap="6px">
                <Text fontSize="xs" color={crm.textFaint}>
                  {shipment ? `Embarque ${shipment}` : ""}
                </Text>
                {canManage && (
                  <Button
                    size="xs"
                    leftIcon={<FaPlus size={9} />}
                    color="white"
                    bg={crm.brand}
                    _hover={{ bg: crm.brandDark }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/saveProcess?fromPo=${po.id}`);
                    }}
                  >
                    Criar processo
                  </Button>
                )}
              </Flex>
            </Box>
          );
        })}
        {purchaseOrders.length === 0 && (
          <Flex
            flex="1"
            flexDir="column"
            align="center"
            justify="center"
            color={crm.textFaint}
            fontSize="sm"
            gap="8px"
            minH="100px"
            opacity={0.7}
          >
            <FaInbox size={20} />
            <Text>Nenhuma PO sem processo</Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default PurchaseOrderColumn;
